/**
 * OperationsDashboard - Platform health and operations management
 *
 * Features:
 * - Overall health status banner
 * - Service health grid
 * - Queue monitor
 * - External provider status
 * - Maintenance mode controls
 * - Emergency actions panel
 * - Auto-refresh (polling + SSE stream) while page is visible
 *
 * Uses real API data only; no mock fallbacks. Shows loading, error, and empty states.
 */
import { useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button, Card, CardContent } from '@respectly/ui';
import { Activity, RefreshCw, AlertCircle } from 'lucide-react';
import { useAuth } from '@respectly/utils/auth';
import { API_ENDPOINTS } from '@respectly/api-client';
import { useOperationsStore } from '../../stores/useOperationsStore';
import {
  HealthStatusGrid,
  QueueMonitor,
  ProviderStatusCard,
  MaintenancePanel,
  EmergencyActionsPanel,
} from '../../components/operations';
import type {
  QueueStats,
} from '@respectly/shared-types';

// Poll every 60 seconds when the operations page is visible
const POLL_INTERVAL_MS = 60_000;
const SSE_RECONNECT_DELAY_MS = 5_000;

const getOperationsStreamUrl = (token: string): string => {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:3000';
  const path = API_ENDPOINTS.SUPER_ADMIN.OPERATIONS.STREAM;
  const separator = path.includes('?') ? '&' : '?';
  return `${base.replace(/\/$/, '')}${path}${separator}token=${encodeURIComponent(token)}`;
};

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

// ============================================
// Component
// ============================================

export function OperationsDashboard() {
  const { getAccessToken } = useAuth();
  const {
    healthStatus,
    healthError,
    isLoadingHealth,
    maintenanceMode,
    maintenanceError,
    isLoadingMaintenance,
    isTogglingMaintenance,
    queueStats,
    queueError,
    isLoadingQueues,
    isPausingQueue,
    isResumingQueue,
    providerStatus,
    providerError,
    isLoadingProviders,
    isClearingCache,
    lastCacheClearAt,
    loadHealthStatus,
    loadMaintenanceMode,
    loadQueueStats,
    loadProviderStatus,
    toggleMaintenanceMode,
    pauseQueue,
    resumeQueue,
    invalidateCache,
  } = useOperationsStore();

  // Single refresh function for manual refresh and polling
  const refreshAll = useCallback(() => {
    loadHealthStatus();
    loadMaintenanceMode();
    loadQueueStats();
    loadProviderStatus();
  }, [loadHealthStatus, loadMaintenanceMode, loadQueueStats, loadProviderStatus]);

  // Load data on mount
  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // Poll for fresh data while the operations page is mounted and tab is visible
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const startPolling = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') {
          refreshAll();
        }
      }, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    startPolling();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stopPolling();
      } else {
        startPolling();
        refreshAll(); // Refresh immediately when tab becomes visible
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshAll]);

  // SSE stream: when connected, server pushes "refresh" every 30s; we refetch. Reconnect on error.
  const sseRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (document.visibilityState !== 'visible') return;

    let mounted = true;

    const connect = () => {
      getAccessToken().then((token) => {
        if (!mounted || !token || document.visibilityState !== 'visible') return;
        const url = getOperationsStreamUrl(token);
        const es = new EventSource(url);
        sseRef.current = es;

        es.addEventListener('refresh', () => {
          refreshAll();
        });

        es.onerror = () => {
          es.close();
          sseRef.current = null;
          if (mounted && document.visibilityState === 'visible') {
            reconnectTimeoutRef.current = setTimeout(connect, SSE_RECONNECT_DELAY_MS);
          }
        };
      });
    };

    connect();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        if (sseRef.current) {
          sseRef.current.close();
          sseRef.current = null;
        }
      } else {
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [getAccessToken, refreshAll]);

  // Real data only – no mock fallbacks. Components handle null/empty and show loading or empty state.
  const queues: QueueStats[] = Array.isArray(queueStats) ? queueStats : [];
  const providers = Array.isArray(providerStatus) ? providerStatus : [];

  const handleRefreshAll = () => {
    refreshAll();
  };

  const pausedQueueCount = queues.filter((q) => q.paused).length;

  const handlePauseAllQueues = async () => {
    // Pause all non-paused queues
    for (const queue of queues.filter((q) => !q.paused)) {
      await pauseQueue(queue.name);
    }
  };

  const handleResumeAllQueues = async () => {
    // Resume all paused queues
    for (const queue of queues.filter((q) => q.paused)) {
      await resumeQueue(queue.name);
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between p-4 lg:p-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                Platform Operations
              </h1>
              <p className="text-sm text-slate-500">
                Health monitoring, maintenance, and system controls
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={handleRefreshAll}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        <div className="space-y-6">
          {/* Health Status Grid */}
          <motion.div variants={itemVariants} className="space-y-2">
            {healthError && (
              <Card className="border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/10">
                <CardContent className="py-3 flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{healthError.message}</span>
                  <Button variant="ghost" size="sm" className="ml-auto" onClick={loadHealthStatus}>
                    Retry
                  </Button>
                </CardContent>
              </Card>
            )}
            <HealthStatusGrid healthStatus={healthStatus} isLoading={isLoadingHealth} />
          </motion.div>

          {/* Two Column Layout for Queues and Providers */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Queue Monitor */}
            <motion.div variants={itemVariants} className="space-y-2">
              {queueError && (
                <Card className="border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/10">
                  <CardContent className="py-3 flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{queueError.message}</span>
                    <Button variant="ghost" size="sm" className="ml-auto" onClick={loadQueueStats}>
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              )}
              <QueueMonitor
                queues={queues}
                isLoading={isLoadingQueues}
                isPausingQueue={isPausingQueue}
                isResumingQueue={isResumingQueue}
                onPauseQueue={pauseQueue}
                onResumeQueue={resumeQueue}
              />
            </motion.div>

            {/* Provider Status */}
            <motion.div variants={itemVariants} className="space-y-2">
              {providerError && (
                <Card className="border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/10">
                  <CardContent className="py-3 flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{providerError.message}</span>
                    <Button variant="ghost" size="sm" className="ml-auto" onClick={loadProviderStatus}>
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              )}
              <ProviderStatusCard
                providers={providers}
                isLoading={isLoadingProviders}
              />
            </motion.div>
          </div>

          {/* Maintenance and Emergency Actions */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Maintenance Panel */}
            <motion.div variants={itemVariants} className="space-y-2">
              {maintenanceError && (
                <Card className="border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/10">
                  <CardContent className="py-3 flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{maintenanceError.message}</span>
                    <Button variant="ghost" size="sm" className="ml-auto" onClick={loadMaintenanceMode}>
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              )}
              <MaintenancePanel
                maintenanceMode={maintenanceMode}
                isLoading={isLoadingMaintenance}
                isToggling={isTogglingMaintenance}
                onToggle={toggleMaintenanceMode}
              />
            </motion.div>

            {/* Emergency Actions */}
            <motion.div variants={itemVariants}>
              <EmergencyActionsPanel
                isClearingCache={isClearingCache}
                lastCacheClearAt={lastCacheClearAt}
                pausedQueueCount={pausedQueueCount}
                totalQueueCount={queues.length}
                onClearCache={invalidateCache}
                onPauseAllQueues={handlePauseAllQueues}
                onResumeAllQueues={handleResumeAllQueues}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
